import type { ArmorType, EquipmentConfig, ShapeType } from '../AppearanceDefinitions';
import type { BodyRig, BodySize } from './HumanoidGeometry';
import { BODY_RIGS } from './HumanoidGeometry';

export type GearFitLayer = 'back' | 'body' | 'head';
export type GearFitMode = 'surface' | 'shell' | 'anchored' | 'free';

export interface GearFitContext {
  shape: ShapeType;
  bodySize: BodySize;
}

export const DEFAULT_GEAR_FIT: GearFitContext = {
  shape: 'classic',
  bodySize: 'normal',
};

export const EMPTY_EQUIPMENT_SLOTS: EquipmentConfig['slots'] = {
  head: 0,
  neck: 0,
  shoulders: 0,
  back: 0,
  chest: 0,
  hands: 0,
  legs: 0,
  feet: 0,
  waist: 0,
  mainHand: 0,
  offHand: 0,
  classItem: 0,
};

export function bodyRigForGear(context: GearFitContext): BodyRig {
  return BODY_RIGS[context.shape];
}

export function resolveLimbGearScale(context: GearFitContext): number {
  // Shape-specific pose anchors move the limb; local glove/boot geometry stays
  // authored around that anchor. Body size wraps both exactly once.
  void context;
  return 1;
}

export function normalizeGearVariant(variant: number): number {
  if (!Number.isFinite(variant)) return 0;
  return Math.max(0, Math.min(5, Math.round(variant)));
}

export function resolveAppendageVariantScale(variant: number): number {
  return Number((1 + normalizeGearVariant(variant) * 0.018).toFixed(3));
}

interface AppendageGeometry {
  glove: string;
  boot: string;
}

/** Local-space silhouettes stay centered on their animated attachment anchor. */
export const APPENDAGE_GEOMETRY: Record<ArmorType, AppendageGeometry> = {
  plate: {
    glove: 'M-7,-5 L1,-7 L7,-2 L6,4 L0,7 L-7,2 Z',
    boot: 'M-10,-6 L5,-7 L13,-1 L10,5 L-10,5 Z',
  },
  robe: {
    glove: 'M-6,-3 Q0,-7 6,-2 L5,4 Q0,7 -6,3 Z',
    boot: 'M-9,-4 Q0,-8 9,-4 L12,1 Q4,6 -9,5 Z',
  },
  leather: {
    glove: 'M-7,-4 Q0,-7 6,-3 L7,1 L4,6 Q-1,7 -7,2 Z',
    boot: 'M-10,-5 Q-1,-9 8,-5 L13,-1 L10,5 L-10,5 Z',
  },
};

export const APPENDAGE_STYLE = {
  outline: '#24272d',
  outlineWidth: 1.2,
  highlight: '#ffffff',
  highlightWidth: 1,
  gloveHighlightPath: 'M-5,-1 L4,-1',
  bootHighlightPath: 'M-8,1 Q1,3 10,1',
  backHandOpacity: 0.9,
  backFootOpacity: 0.86,
  gloveHighlightOpacity: 0.22,
  bootHighlightOpacity: 0.2,
} as const;

export const GEAR_COLORS = {
  outline: '#24272d',
  plateSecondary: '#444',
  plateDarkOutline: '#222',
  robeSecondary: '#222',
  robeMantle: '#4B0082',
  rogueSecondary: '#111',
  leatherSecondary: '#462719',
  leatherStrap: '#3e2723',
  leatherOutline: '#25221f',
  highlight: '#ffffff',
} as const;

// ─── Resolved-descriptor family registry (BIG-EQUIP-VIS-01 S2) ────
// Maps authored family strings to appendage geometry for the anchored
// feet/hands slots. Each family maps to a { glove, boot } geometry pair
// from APPENDAGE_GEOMETRY. Unknown families fail closed (no entry → no
// geometry). This registry is exhaustive against the canonical
// EQUIPMENT_VISUAL_FAMILIES for hands and feet.

export const RESOLVED_APPENDAGE_FAMILIES: Record<string, AppendageGeometry> = {
  // hands
  gloves: APPENDAGE_GEOMETRY.leather,
  gauntlets: APPENDAGE_GEOMETRY.plate,
  // feet
  boots: APPENDAGE_GEOMETRY.leather,
  sabatons: APPENDAGE_GEOMETRY.plate,
  slippers: APPENDAGE_GEOMETRY.robe,
  moccasins: APPENDAGE_GEOMETRY.leather,
};

/**
 * Multiply-darken a hex color (#rgb or #rrggbb) by a factor (0..1).
 * Three-digit #rgb is expanded to #rrggbb before the factor is applied.
 * Output is always 6-digit #rrggbb. Clamps factor to [0, 1].
 *
 * Examples:
 *   darken('#ffffff', 0.5) === '#808080'
 *   darken('#abc', 0.5)    === '#555e66'
 */
export function darken(hex: string, factor: number): string {
  const clamped = Math.max(0, Math.min(1, factor));
  let h = hex.replace('#', '');
  // Expand 3-digit #rgb to 6-digit #rrggbb.
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  if (h.length !== 6) return hex; // not a valid hex color; return unchanged
  const n = parseInt(h, 16);
  if (Number.isNaN(n)) return hex;
  const r = Math.round(((n >> 16) & 0xff) * clamped);
  const g = Math.round(((n >> 8) & 0xff) * clamped);
  const b = Math.round((n & 0xff) * clamped);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}
